import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Circle, CheckCircle2, MapPin, CalendarClock, RefreshCw, Bell } from "lucide-react";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Tag, EmptyState } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { getDriverContact } from "@/lib/driverContact";
import { pushAvailable, pushPermission, enablePush } from "@/lib/push";
import { TIMELINE_STAGES, fmtDate, fmtDateTime, REQUEST_LABEL } from "@/lib/vda";

async function load(id) {
  const { data: bookings } = await supabase.from("v_driver_bookings").select("*").eq("id", id).limit(1);
  const booking = bookings?.[0] || null;
  const { data: updates } = await supabase.from("v_public_updates").select("*").eq("booking_request_id", id).order("created_at", { ascending: false });
  return { booking, updates: updates || [] };
}

export default function DriverBookingDetail() {
  const { id } = useParams();
  const { data, loading, reload } = useAsync(() => load(id), [id]);
  const [saving, setSaving] = useState(false);
  const [chasing, setChasing] = useState(false);
  const [pushCfg, setPushCfg] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => { getDriverContact().then(setPushCfg).catch(() => {}); }, []);

  if (loading) return <DriverShell back="/driver/history"><Spinner /></DriverShell>;
  const b = data.booking;
  if (!b) return <DriverShell back="/driver/history"><EmptyState title="Booking not found" /></DriverShell>;

  const currentIdx = TIMELINE_STAGES.indexOf(b.public_status);
  const stages = TIMELINE_STAGES.slice(0, Math.max(currentIdx + 2, 4));
  const isOpen = b.public_status !== "Completed";
  const canDropoff = !b.dropped_off_at && !["Completed", "Ready for collection"].includes(b.public_status);
  const canPush = pushAvailable(pushCfg) && pushPermission() !== "granted";

  const confirmDropoff = async () => {
    setSaving(true);
    try {
      await api.driverDropoff(id);
      toast.success("Thanks — we've recorded your drop-off.");
      reload();
    } catch (err) {
      toast.error(err.message || "Could not record drop-off.");
    } finally { setSaving(false); }
  };

  const requestUpdate = async () => {
    setChasing(true);
    try {
      await api.driverChase(id);
      toast.success("We've let your Venson team know you'd like an update.");
      reload();
    } catch (err) {
      toast.info(err.message || "Could not request an update right now.");
    } finally { setChasing(false); }
  };

  const enableReminders = async () => {
    setPushBusy(true);
    try {
      await enablePush(pushCfg);
      toast.success("We'll remind you about this booking.");
      setPushCfg({ ...pushCfg });
    } catch (err) {
      toast.info(err.message || "Couldn't enable reminders.");
    } finally { setPushBusy(false); }
  };

  return (
    <DriverShell back="/driver/history">
      <div className="flex items-center justify-between">
        <div>
          <div className="vda-reg text-xs text-muted-foreground">{b.human_reference}</div>
          <h1 className="text-xl font-semibold text-primary mt-0.5">{REQUEST_LABEL[b.request_type] || b.request_type}</h1>
        </div>
        <Tag tone={b.public_status === "Completed" ? "muted" : "green"}>{b.public_status}</Tag>
      </div>

      <Panel className="mt-4 p-4 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">Vehicle</span><span className="vda-reg">{b.registration}</span></div>
        {b.booking_type && <div className="flex justify-between" data-testid="driver-row-booking-type"><span className="text-muted-foreground">Booking type</span><span className="text-right">{b.booking_type}</span></div>}
        <div className="flex justify-between"><span className="text-muted-foreground">Appointment</span><span>{fmtDateTime(b.appointment_at)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Expected completion</span><span>{fmtDate(b.expected_completion_at)}</span></div>
        {b.garage_name && <div className="flex justify-between" data-testid="driver-row-garage"><span className="text-muted-foreground">Garage</span><span className="text-right">{b.garage_name}</span></div>}
        {b.garage_phone && <div className="flex justify-between" data-testid="driver-row-garage-phone"><span className="text-muted-foreground">Garage phone</span><span className="text-right">{b.garage_phone}</span></div>}
        {typeof b.courtesy_vehicle_confirmed === "boolean" && <div className="flex justify-between" data-testid="driver-row-courtesy"><span className="text-muted-foreground">Courtesy vehicle</span><span className="text-right">{b.courtesy_vehicle_confirmed ? "Confirmed" : "Not provided"}</span></div>}
        {b.public_work_summary && <div className="pt-1" data-testid="driver-row-work"><span className="text-muted-foreground">Work</span><p className="mt-0.5">{b.public_work_summary}</p></div>}
      </Panel>

      {b.description && (
        <Panel className="mt-4 p-4" data-testid="your-enquiry">
          <h2 className="vda-tick text-muted-foreground">{b.request_type === "other" ? "Your enquiry" : "Your request"}</h2>
          <p className="mt-1.5 text-sm whitespace-pre-wrap">{b.description}</p>
          <p className="mt-2 text-[11px] text-muted-foreground">This is the message you submitted. Your Venson team can see it too.</p>
        </Panel>
      )}


      {canDropoff && (
        <Btn variant="clay" data-testid="confirm-dropoff" onClick={confirmDropoff} loading={saving} className="w-full mt-4">
          <MapPin className="h-4 w-4" /> Confirm vehicle dropped off
        </Btn>
      )}

      {isOpen && (
        <div className="mt-3 space-y-2">
          <Btn variant="outline" data-testid="request-update-btn" onClick={requestUpdate} loading={chasing} className="w-full">
            <RefreshCw className="h-4 w-4" /> Request an update
          </Btn>
          {canPush && (
            <Btn variant="ghost" data-testid="enable-reminders-btn" onClick={enableReminders} loading={pushBusy} className="w-full text-muted-foreground">
              <Bell className="h-4 w-4" /> Remind me about this booking
            </Btn>
          )}
        </div>
      )}

      <h2 className="mt-7 font-semibold">Progress</h2>
      <Panel className="mt-3 p-4">
        <ol className="relative">
          {stages.map((s, i) => {
            const done = i <= currentIdx;
            const active = i === currentIdx;
            return (
              <li key={s} className="flex gap-3 pb-4 last:pb-0" data-testid={`stage-${i}`}>
                <div className="flex flex-col items-center">
                  {done ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Circle className="h-5 w-5 text-muted-foreground/40" />}
                  {i < stages.length - 1 && <span className={`w-px flex-1 mt-1 ${done ? "bg-primary" : "bg-border"}`} />}
                </div>
                <span className={`text-sm ${active ? "font-semibold text-primary" : done ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
              </li>
            );
          })}
        </ol>
      </Panel>

      <h2 className="mt-7 font-semibold flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Updates</h2>
      <div className="mt-3 space-y-2">
        {data.updates.length === 0 && <p className="text-sm text-muted-foreground">No updates yet.</p>}
        {data.updates.map((u) => (
          <Panel key={u.id} className="p-3" data-testid="public-update">
            <div className="flex items-center justify-between">
              {u.public_status ? <Tag tone="green">{u.public_status}</Tag> : <span className="text-xs text-muted-foreground">Update</span>}
              <span className="text-xs text-muted-foreground">{fmtDateTime(u.created_at)}</span>
            </div>
            {u.public_message && <p className="text-sm mt-2">{u.public_message}</p>}
          </Panel>
        ))}
      </div>
    </DriverShell>
  );
}
